// API client configuration
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
let sessionExpiredRedirected = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Single-flight redirect: prevent multiple parallel 401s (e.g. Compliance
      // page firing 3+ admin calls simultaneously on token expiry) from racing
      // into a broken login flow.
      if (!sessionExpiredRedirected) {
        sessionExpiredRedirected = true;
        localStorage.removeItem('access_token');
        const onLoginPage = window.location.pathname === '/login';
        if (!onLoginPage) {
          // Tell the login page to show a friendly "session expired" banner.
          try {
            sessionStorage.setItem('session_expired', '1');
          } catch (_) {}
          window.location.href = '/login?reason=session_expired';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  setupMFA: () => api.post('/auth/mfa/setup'),
  verifyMFA: (token) => api.post('/auth/mfa/verify', { token }),
  getCurrentUser: () => api.get('/auth/me'),
};

// SSO API (Login.gov, Okta Gov, Azure AD Gov)
export const ssoAPI = {
  listProviders: () => api.get('/auth/sso/providers'),
  initiateLogin: (provider) => api.get(`/auth/sso/${provider}/login`),
};

// Settings API (profile, security, notifications, sessions)
export const settingsAPI = {
  getMe: () => api.get('/settings/me'),
  updateProfile: (data) => api.put('/settings/profile', data),
  updateEmail: (data) => api.put('/settings/email', data),
  changePassword: (data) => api.post('/settings/password', data),
  enrollMFA: () => api.post('/settings/mfa/enroll'),
  activateMFA: (token) => api.post('/settings/mfa/activate', { mfa_token: token }),
  disableMFA: (data) => api.post('/settings/mfa/disable', data),
  backupCodesStatus: () => api.get('/settings/mfa/backup-codes/status'),
  regenerateBackupCodes: (token) =>
    api.post('/settings/mfa/backup-codes/regenerate', { mfa_token: token }),
  getNotifications: () => api.get('/settings/notifications'),
  updateNotifications: (data) => api.put('/settings/notifications', data),
  getActiveSession: () => api.get('/settings/sessions/active'),
  revokeAllSessions: () => api.post('/settings/sessions/revoke-all'),
};

// Lifecycle Funds API
export const lifecycleAPI = {
  list: () => api.get('/funds/lifecycle'),
  suggest: (currentAge, retirementAge) =>
    api.get('/funds/lifecycle/suggest', {
      params: { current_age: currentAge, retirement_age: retirementAge },
    }),
};

// Retirement Scenarios API
export const scenariosAPI = {
  list: () => api.get('/retirement-scenarios'),
  create: (data) => api.post('/retirement-scenarios', data),
  remove: (id) => api.delete(`/retirement-scenarios/${id}`),
};

// Statements API
export const statementsAPI = {
  list: () => api.get('/participant/statements'),
  generate: (period, year, index) =>
    api.post('/participant/statements/generate', { period, year, index }),
  downloadUrl: (id) =>
    `${api.defaults.baseURL}/participant/statements/${id}/download`,
  exportSnapshot: () =>
    api.post('/participant/export-snapshot', null, { responseType: 'blob' }),
};

// Participant API
export const participantAPI = {
  getMyInfo: () => api.get('/participants/me/info'),
  getById: (id) => api.get(`/participants/${id}`),
  create: (data) => api.post('/participants', data),
  selfRegister: (data) => api.post('/participants/self-register', data),
};

// Account API
export const accountAPI = {
  getMyAccount: () => api.get('/accounts/me'),
  getBalance: (accountId) => api.get(`/accounts/${accountId}/balance`),
  updateAllocations: (accountId, allocations) =>
    api.post(`/accounts/${accountId}/allocations`, allocations),
};

// Contribution API
export const contributionAPI = {
  getMyContributions: (params) => api.get('/contributions/me', { params }),
  processContribution: (data) => api.post('/contributions', data),
};

// Ledger API
export const ledgerAPI = {
  getAccountLedger: (accountId, limit = 100) =>
    api.get(`/ledger/account/${accountId}`, { params: { limit } }),
  verifyLedger: (accountId) => api.get(`/ledger/verify/${accountId}`),
};

// Audit API
export const auditAPI = {
  getLogs: (params) => api.get('/audit/logs', { params }),
};

// Withdrawal Policy + Payroll Adapter Registry API (Phase 4D)
export const policyAPI = {
  // Admin — withdrawal policy
  listPolicies: () => api.get('/admin/withdrawal-policy'),
  upsertPolicy: (data) => api.put('/admin/withdrawal-policy', data),
  // Participant — policy check
  myCheck: (kind = 'any') =>
    api.get('/participant/withdrawal-policy/check', {
      params: { withdrawal_kind: kind },
    }),
};

export const payrollAPI = {
  list: (params) => api.get('/admin/payroll-adapters', { params }),
  create: (data) => api.post('/admin/payroll-adapters', data),
  patch: (id, data) => api.patch(`/admin/payroll-adapters/${id}`, data),
  summary: () => api.get('/admin/payroll-adapters/summary'),
};
